'use client';

import type { FormEvent, RefObject } from 'react';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { normalizeUsPhoneToE164, SMS_MARKETING_DISCLOSURE } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';
import { SDSTextInput } from '@repo/sds/SDSTextInput';

import { SmsCodeActions, SmsCodeEntry } from './sms-code-fields';
import { SmsConsentCheckbox, SmsNotAConditionNotice, SmsPrivacyNotice } from './sms-marketing-disclosure';
import type {
  SmsMarketingCodeRequestResult,
  SmsMarketingPendingVerification,
  SmsMarketingSignupRequest,
  SmsMarketingVerifyResult,
} from './sms-marketing-signup-types';
import { formatUsPhoneForDisplay } from './us-phone-input';
import {
  type SmsMarketingSignupDetails,
  type SmsMarketingSignupFlow,
  type SmsMarketingSignupStep,
  useSmsMarketingSignupFlow,
} from './use-sms-marketing-signup-flow';

const ids = DataTestIds.SMS_MARKETING;
const EMAIL_SCHEMA = z.email();

export interface SmsMarketingSignupProps {
  /** Only the public main page asks for an email; signed-in surfaces use the account email on the server. */
  collectEmail: boolean;
  /** Account settings can verify a number without subscribing to texts; the checkbox stays unchecked by default either way. */
  requireConsent?: boolean;
  submitLabel?: string;
  /** Resumes the code step, including when it arrives after the first render, unless the person already started. */
  pendingVerification?: SmsMarketingPendingVerification;
  onSubmit: (request: SmsMarketingSignupRequest) => Promise<SmsMarketingCodeRequestResult>;
  onVerify: (code: string) => Promise<SmsMarketingVerifyResult>;
  onResend: () => Promise<SmsMarketingCodeRequestResult>;
  /** Called once the number is proven, whether the signup is new or already existed. */
  onVerified?: () => void;
  /** The person dropped the pending code to enter a different number. */
  onStartOver?: () => void;
}

/**
 * Phone number, explicit SMS consent and code verification as a standalone form,
 * used by the main page and account settings. The surface supplies the server calls.
 */
export function SmsMarketingSignup({
  collectEmail,
  requireConsent = true,
  submitLabel,
  pendingVerification,
  onSubmit,
  onVerify,
  onResend,
  onVerified,
  onStartOver,
}: SmsMarketingSignupProps) {
  const { t } = useTranslation('common');
  const codeRef = useRef<HTMLDivElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const flow = useSmsMarketingSignupFlow({
    codeRef,
    resultRef,
    pendingVerification,
    onSubmit,
    onVerify,
    onResend,
    onVerified,
    onStartOver,
  });
  const { step, failure } = flow;

  return (
    <SDSFlexbox direction="column" gap="no-design-16" dataTestId={ids.SIGNUP}>
      {step.kind === 'details' && (
        <DetailsStep
          collectEmail={collectEmail}
          requireConsent={requireConsent}
          details={flow.details}
          isBusy={flow.busyAction !== undefined}
          submitLabel={submitLabel ?? t('smsMarketing.submit')}
          onChange={flow.updateDetails}
          onSubmit={request => void flow.submit(request)}
        />
      )}
      {step.kind === 'code' && (
        <CodeStep flow={flow} codeRef={codeRef} maskedPhone={step.maskedPhone} resendAt={step.resendAvailableAt} />
      )}
      {(step.kind === 'verified' || step.kind === 'already-enrolled') && (
        <div ref={resultRef} tabIndex={-1} data-testid={ids.RESULT_MESSAGE}>
          <SDSText type="body-medium" text={t(smsMarketingResultMessageKey(step))} />
        </div>
      )}
      <div role="alert" data-testid={ids.ERROR_MESSAGE}>
        {failure && <SDSText type="body-small" color="alert" text={t(`smsMarketing.errors.${failure}`)} />}
      </div>
    </SDSFlexbox>
  );
}

export function smsMarketingResultMessageKey(
  step: Extract<SmsMarketingSignupStep, { kind: 'verified' | 'already-enrolled' }>,
): 'smsMarketing.verified' | 'smsMarketing.numberVerified' | 'smsMarketing.alreadyEnrolled' {
  if (step.kind === 'already-enrolled') {
    return 'smsMarketing.alreadyEnrolled';
  }
  return step.subscribed ? 'smsMarketing.verified' : 'smsMarketing.numberVerified';
}

interface DetailsStepProps {
  collectEmail: boolean;
  requireConsent: boolean;
  details: SmsMarketingSignupDetails;
  isBusy: boolean;
  submitLabel: string;
  onChange: (update: Partial<SmsMarketingSignupDetails>) => void;
  onSubmit: (request: SmsMarketingSignupRequest) => void;
}

function DetailsStep({
  collectEmail,
  requireConsent,
  details,
  isBusy,
  submitLabel,
  onChange,
  onSubmit,
}: DetailsStepProps) {
  const { t } = useTranslation('common');
  const phoneHintId = useId();
  const [touched, setTouched] = useState({ email: false, phone: false });
  const email = details.email.trim();
  const phoneE164 = normalizeUsPhoneToE164(details.phone);
  const isEmailValid = !collectEmail || EMAIL_SCHEMA.safeParse(email).success;
  const isComplete = Boolean(phoneE164) && isEmailValid && (details.consented || !requireConsent);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (isBusy || !(isComplete && phoneE164)) {
      return;
    }
    onSubmit({
      ...(collectEmail ? { email } : {}),
      phoneE164,
      consent: details.consented,
      disclosureVersion: SMS_MARKETING_DISCLOSURE.version,
    });
  }

  function leavePhoneField(): void {
    setTouched(current => ({ ...current, phone: true }));
    // Formatting waits for the field to lose focus, so typing and pasting never move the caret or drop digits.
    if (phoneE164) {
      onChange({ phone: formatUsPhoneForDisplay(phoneE164) });
    }
  }

  return (
    <form noValidate onSubmit={submit}>
      <SDSFlexbox direction="column" gap="no-design-16">
        {collectEmail && (
          <SDSTextInput
            label={t('smsMarketing.emailLabel')}
            placeholder={t('smsMarketing.emailPlaceholder')}
            name="email"
            value={details.email}
            isRequired
            isFullWidth
            isDisabled={isBusy}
            errorMessage={touched.email && !isEmailValid ? t('smsMarketing.emailInvalid') : undefined}
            onChange={value => onChange({ email: value })}
            onBlur={() => setTouched(current => ({ ...current, email: true }))}
            dataTestId={ids.EMAIL_INPUT}
          />
        )}
        <SDSFlexbox direction="column" gap="no-design-4">
          <SDSTextInput
            label={t('smsMarketing.phoneLabel')}
            placeholder={t('smsMarketing.phonePlaceholder')}
            name="phone"
            value={details.phone}
            isRequired
            isFullWidth
            isDisabled={isBusy}
            ariaDescribedBy={phoneHintId}
            errorMessage={touched.phone && details.phone && !phoneE164 ? t('smsMarketing.phoneInvalid') : undefined}
            onChange={value => onChange({ phone: value })}
            onBlur={leavePhoneField}
            dataTestId={ids.PHONE_INPUT}
          />
          <SDSText id={phoneHintId} type="body-small" color="muted" text={t('smsMarketing.phoneHint')} />
        </SDSFlexbox>
        <SmsConsentCheckbox
          isChecked={details.consented}
          isDisabled={isBusy}
          onChange={checked => onChange({ consented: checked })}
        />
        <SmsNotAConditionNotice />
        <SDSButton
          type="submit"
          text={submitLabel}
          isDisabled={!(isComplete || isBusy)}
          isSubmitting={isBusy}
          isFullWidth
          dataTestId={ids.SUBMIT_BUTTON}
        />
        <SmsPrivacyNotice />
      </SDSFlexbox>
    </form>
  );
}

interface CodeStepProps {
  flow: SmsMarketingSignupFlow;
  codeRef: RefObject<HTMLDivElement | null>;
  maskedPhone: string;
  resendAt: Date;
}

function CodeStep({ flow, codeRef, maskedPhone, resendAt }: CodeStepProps) {
  const { t } = useTranslation('common');
  const { busyAction, isCodeComplete } = flow;
  const isBusy = busyAction !== undefined;
  const isVerifying = busyAction === 'verify';

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (isCodeComplete && !isBusy) {
      void flow.verify();
    }
  }

  return (
    <form noValidate onSubmit={submit}>
      <SDSFlexbox direction="column" gap="no-design-16">
        <SmsCodeEntry
          code={flow.code}
          codeRef={codeRef}
          maskedPhone={maskedPhone}
          isDisabled={isBusy}
          onChangeCode={flow.updateCode}
        />
        <SDSButton
          type="submit"
          text={t('smsMarketing.verify')}
          isDisabled={!isVerifying && (!isCodeComplete || isBusy)}
          isSubmitting={isVerifying}
          isFullWidth
          dataTestId={ids.VERIFY_BUTTON}
        />
        <SmsCodeActions
          busyAction={busyAction}
          resendAvailableAt={resendAt}
          onResend={() => void flow.resend()}
          onChangeNumber={flow.changeNumber}
        />
      </SDSFlexbox>
    </form>
  );
}
